import { Box } from '@chronosphereio/chrono-ui';
import React, { useRef } from 'react';

interface SelectableOverlayProps {
  /** Total height of the overlay container in pixels */
  height: number;
  /** Total width of the overlay container in pixels */
  width: number;
  /** Horizontal offset from the left edge where selection should start (e.g., to exclude labels), in pixels */
  leftOffset: number;
  /** Content to render within the overlay */
  children: React.ReactNode;
  /** Callback fired when user completes a drag selection, receives start and end positions in pixels relative to the selectable area */
  onSelectRegion: ({ start, end }: { start: number; end: number }) => void;
}

/**
 * An interactive overlay that allows users to drag-select a horizontal region.
 * Displays a semi-transparent shaded box during selection and reports the selected
 * pixel range when the drag is completed.
 */
export const SelectableOverlay = ({ height, width, children, onSelectRegion, leftOffset }: SelectableOverlayProps) => {
  const shadeBoxRef = useRef<HTMLDivElement>(null);
  const mouseXStart = useRef<number>();
  const mouseXEnd = useRef<number>();

  const onMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const distanceFromLeft = e.clientX - rect.left;

    if (distanceFromLeft > leftOffset && distanceFromLeft < width) {
      mouseXStart.current = distanceFromLeft - leftOffset;
    }
  };

  const onMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (mouseXStart.current === undefined) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const distanceFromLeft = e.clientX - rect.left;

    // Clamp mouseXEnd to valid bounds to prevent out-of-range time calculations
    const visualizationWidth = width - leftOffset;
    const rawPosition = distanceFromLeft - leftOffset;
    mouseXEnd.current = Math.max(0, Math.min(visualizationWidth, rawPosition));

    // Update shadeBox visual to match the clamped position, even when mouse is outside bounds
    if (shadeBoxRef.current) {
      const leftPos = leftOffset + Math.min(mouseXStart.current, mouseXEnd.current);
      shadeBoxRef.current.style.left = `${leftPos}px`;
      shadeBoxRef.current.style.width = `${Math.abs(mouseXEnd.current - mouseXStart.current)}px`;
    }
  };

  const onMouseEnd: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (shadeBoxRef.current) {
      e.stopPropagation();

      if (mouseXStart.current !== undefined && mouseXEnd.current !== undefined) {
        onSelectRegion({
          start: mouseXStart.current,
          end: mouseXEnd.current,
        });
      }

      mouseXStart.current = undefined;
      mouseXEnd.current = undefined;
      shadeBoxRef.current.style.left = '';
      shadeBoxRef.current.style.width = '';
    }
  };

  const onClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    // Prevent click events from bubbling to parent handlers (e.g., tooltip pinning)
    // This stops the click event that fires after mouseup from reaching PanelTooltipWrapper
    e.stopPropagation();
  };

  return (
    <Box sx={{ position: 'relative', height, width }}>
      <Box
        ref={shadeBoxRef}
        sx={{
          position: 'absolute',
          height,
          backgroundColor: (t) => t.palette.secondary.light,
          opacity: 0.25,
          zIndex: 3,
          pointerEvents: 'none',
        }}
        aria-label="selected area"
      />
      <Box
        sx={{
          position: 'absolute',
          height,
          width,
          zIndex: 2,
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseEnd}
        onMouseLeave={onMouseEnd}
        onClick={onClick}
        aria-label="selectable area"
      >
        {children}
      </Box>
    </Box>
  );
};
